import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FINANCING_KIND_LABELS,
  FINANCING_OBJECT_LABELS,
  FINANCING_STATUSES,
  FINANCING_STATUS_LABELS,
  INSTALLMENT_KIND_LABELS,
  formatCents,
  type CreateFinancingInput,
  type FinancingDetail,
  type FinancingStatus,
} from '@kontor/shared';
import { api, ApiError } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { RecordDocuments } from '../../components/RecordDocuments';
import { useSession } from '../../session';
import { FinancingForm } from './FinancingForm';
import { errorText, formatDay, percentText } from './shared';
import '../fleet/fleet.css';
import './financing.css';

import { EntityHistory } from '../../components/EntityHistory';
const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date());

/** Vertrag (KON-253): Eckdaten, Zahlungsplan mit Zins, Tilgung und Restschuld, Raten als bezahlt vermerken, Vertragsdokumente */
export function FinancingDetailPage() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const mayWrite = can('financing.write');

  const detail = useQuery({ queryKey: ['financing', id], queryFn: async () => (await api<{ data: FinancingDetail }>('GET', `/financing/${id}`)).data, retry: (n, e) => !(e instanceof ApiError && e.status === 404) && n < 2 });
  const refresh = () => {
    for (const key of ['financing', 'financing-list', 'financing-overview', 'reports']) client.invalidateQueries({ queryKey: [key] });
  };
  const save = useMutation({
    mutationFn: (body: CreateFinancingInput) => api('PATCH', `/financing/${id}`, body),
    onSuccess: () => {
      refresh();
      setEditing(false);
      setMessage({ ok: true, text: 'Die Änderungen sind gespeichert.' });
    },
    onError: (e) => setError(errorText(e, 'Das Speichern hat nicht geklappt.')),
  });
  const setStatus = useMutation({
    mutationFn: (status: FinancingStatus) => api('PATCH', `/financing/${id}`, { status }),
    onSuccess: () => {
      refresh();
      setMessage({ ok: true, text: 'Der Stand ist geändert.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Der Stand ließ sich nicht ändern.') }),
  });
  const mark = useMutation({
    mutationFn: (v: { installmentId: string; paid: boolean }) => api('POST', `/financing/${id}/installments/${v.installmentId}/paid`, { paid: v.paid }),
    onSuccess: refresh,
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das hat nicht geklappt.') }),
  });
  const markUntil = useMutation({
    mutationFn: () => api('POST', `/financing/${id}/installments/paid-until`, { until: today() }),
    onSuccess: () => {
      refresh();
      setMessage({ ok: true, text: 'Alle fälligen Raten sind als bezahlt vermerkt.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das hat nicht geklappt.') }),
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/financing/${id}`),
    onSuccess: () => {
      refresh();
      navigate('/finanzierungen');
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Löschen hat nicht geklappt.') }),
  });

  const f = detail.data;
  if (detail.isError) return <div className="alert alert--error">{errorText(detail.error, 'Der Vertrag konnte nicht geladen werden.')}</div>;
  if (!f) return <p className="muted">Wird geladen …</p>;
  const s = f.summary;
  const isLease = f.kind === 'lease';
  const row = (label: string, value: string | null) => (value ? <><dt>{label}</dt><dd>{value}</dd></> : null);

  return (
    <>
      <div className="page-head"><h1>{f.title}</h1><span className="doc-kind">{FINANCING_STATUS_LABELS[f.status]}</span></div>
      <p className="muted"><Link to="/finanzierungen">← Alle Verträge</Link> · {f.number} · {FINANCING_KIND_LABELS[f.kind]} · {f.lenderName}</p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {s.overdueCount > 0 && <div className="alert alert--warn" role="status">{s.overdueCount} {s.overdueCount === 1 ? 'Rate ist' : 'Raten sind'} fällig und nicht als bezahlt vermerkt.</div>}

      <div className="fleet-tiles">
        <div className="card fleet-tile"><span className="muted small">Rate</span><strong>{formatCents(f.installmentCents)}</strong><span className="muted small">brutto monatlich</span></div>
        <div className="card fleet-tile"><span className="muted small">{isLease ? 'Restverpflichtung' : 'Restschuld'}</span><strong>{formatCents(s.debtCents)}</strong></div>
        <div className="card fleet-tile"><span className="muted small">Bezahlt</span><strong>{s.paidCount} von {s.count}</strong><span className="muted small">{formatCents(s.paidCents)}</span></div>
        <div className="card fleet-tile"><span className="muted small">Nächste Zahlung</span><strong>{s.nextDueDate ? formatDay(s.nextDueDate) : '–'}</strong>{s.nextPaymentCents !== null && <span className="muted small">{formatCents(s.nextPaymentCents)}</span>}</div>
        <div className="card fleet-tile"><span className="muted small">{isLease ? 'Kosten insgesamt (netto)' : 'Zinsen insgesamt'}</span><strong>{formatCents(isLease ? s.totalCostCents : s.totalInterestCents)}</strong><span className="muted small">{isLease ? 'Raten und Sonderzahlung' : 'dazu Gebühren, die Tilgung ist keine Ausgabe'}</span></div>
        <div className="card fleet-tile"><span className="muted small">Vertragsende</span><strong>{formatDay(s.endDate)}</strong></div>
      </div>

      {editing ? (
        <FinancingForm financing={f} onSubmit={(b) => { setError(null); save.mutate(b); }} onCancel={() => setEditing(false)} pending={save.isPending} error={error} />
      ) : (
        <CollapsibleCard title="Vertrag" storageKey="financing-general" defaultOpen>
          <dl className="inv-facts">
            {row('Art', FINANCING_KIND_LABELS[f.kind])}
            {row('Geldgeber', f.lenderName)}
            {row('Vertragsnummer', f.contractNumber)}
            {row('Gegenstand', f.vehicleId ? `${FINANCING_OBJECT_LABELS[f.objectKind]} ${f.vehiclePlate ?? ''}`.trim() : [FINANCING_OBJECT_LABELS[f.objectKind], f.objectDescription].filter(Boolean).join(', '))}
            {row('Vertragsbeginn (erste Rate)', formatDay(f.startDate))}
            {row('Laufzeit', `${f.termMonths} Monate`)}
            {f.principalCents !== null && row(f.kind === 'loan' ? 'Darlehensbetrag' : 'Finanzierter Kaufpreis', formatCents(f.principalCents))}
            {f.annualRateBp !== null && row('Sollzins', percentText(f.annualRateBp))}
            {f.downPaymentCents > 0 && row('Sonderzahlung', `${formatCents(f.downPaymentCents)} brutto`)}
            {f.balloonCents > 0 && row(isLease ? 'Restwert' : 'Schlussrate', formatCents(f.balloonCents))}
            {f.feesCents > 0 && row('Gebühren', formatCents(f.feesCents))}
            {isLease && row('Umsatzsteuer', `${f.taxRate} %${f.inputTaxDeductible ? ', Vorsteuer abziehbar' : ', Vorsteuer nicht abziehbar'}`)}
            {f.mileagePerYearKm !== null && row('Freikilometer', `${f.mileagePerYearKm.toLocaleString('de-DE')} km pro Jahr${f.excessKmCents !== null ? `, Mehrkilometer ${f.excessKmCents} Cent je km` : ''}`)}
            {row('Zahlung', f.directDebit ? 'Lastschrift, gilt ab dem Fälligkeitstag als bezahlt' : 'Überweisung, Raten werden von Hand als bezahlt vermerkt')}
            {row('Sachkonto', f.ledgerAccount)}
            {row('Bemerkung', f.note)}
          </dl>
          <p className="muted small">{f.expenseNote}</p>
          {mayWrite && (
            <div className="fin-actions">
              <button type="button" className="btn btn--primary" onClick={() => { setError(null); setMessage(null); setEditing(true); }}>Bearbeiten</button>
              <label className="fin-status">
                <span className="muted small">Stand</span>
                <select
                  className="input fleet-year"
                  value={f.status}
                  onChange={(e) => {
                    const next = e.target.value as FinancingStatus;
                    if (next === 'terminated' && !window.confirm('Den Vertrag als vorzeitig beendet markieren? Raten, die noch nicht fällig und nicht bezahlt sind, entfallen aus dem Zahlungsplan und aus den Vorschauen.')) return;
                    setStatus.mutate(next);
                  }}
                >
                  {FINANCING_STATUSES.map((st) => <option key={st} value={st}>{FINANCING_STATUS_LABELS[st]}</option>)}
                </select>
              </label>
              {can('financing.delete') && <button type="button" className="btn btn--danger" onClick={() => window.confirm('Diesen Vertrag mit dem Zahlungsplan löschen? Bereits in Berichten gezählter Aufwand entfällt damit.') && remove.mutate()}>Löschen</button>}
            </div>
          )}
        </CollapsibleCard>
      )}

      <CollapsibleCard title="Zahlungsplan" storageKey="financing-schedule" defaultOpen>
        {mayWrite && !f.directDebit && s.overdueCount > 0 && (
          <p><button type="button" className="btn btn--secondary btn--sm" disabled={markUntil.isPending} onClick={() => markUntil.mutate()}>Alle fälligen Raten als bezahlt vermerken</button></p>
        )}
        <div className="table-card">
          <table className="table table--cards fin-schedule">
            <thead><tr><th>Nr.</th><th>Fällig</th><th>Art</th><th className="num">Zahlung</th>{!isLease && <th className="num">Zinsen</th>}{!isLease && <th className="num">Tilgung</th>}{isLease && <th className="num">Steuer</th>}<th className="num">{isLease ? 'Restverpflichtung' : 'Restschuld'}</th><th>Stand</th><th /></tr></thead>
            <tbody>
              {f.installments.map((i) => (
                <tr key={i.id} className={i.state === 'overdue' ? 'fin-overdue' : i.state === 'paid' ? 'fin-paid' : ''}>
                  <td data-label="Nr.">{i.seq}</td>
                  <td data-label="Fällig">{formatDay(i.dueDate)}</td>
                  <td data-label="Art">{INSTALLMENT_KIND_LABELS[i.kind]}</td>
                  <td data-label="Zahlung" className="num">{formatCents(i.paymentCents)}</td>
                  {!isLease && <td data-label="Zinsen" className="num">{formatCents(i.interestCents)}</td>}
                  {!isLease && <td data-label="Tilgung" className="num">{formatCents(i.principalCents)}</td>}
                  {isLease && <td data-label="Steuer" className="num">{formatCents(i.taxCents)}</td>}
                  <td data-label={isLease ? 'Restverpflichtung' : 'Restschuld'} className="num">{formatCents(i.balanceCents)}</td>
                  <td data-label="Stand">
                    {i.state === 'paid' ? <>bezahlt am {formatDay(i.paidOn)}{i.paidCents !== null && <div className="muted small">{formatCents(i.paidCents)} gezahlt</div>}</> : i.state === 'overdue' ? <strong className="fleet-warn">überfällig</strong> : <span className="muted">offen</span>}
                  </td>
                  <td className="doc-actions">
                    {mayWrite && i.state !== 'paid' && <button type="button" className="btn btn--secondary btn--sm" disabled={mark.isPending} onClick={() => mark.mutate({ installmentId: i.id, paid: true })}>Bezahlt</button>}
                    {mayWrite && i.marked && <button type="button" className="btn btn--secondary btn--sm" disabled={mark.isPending} onClick={() => mark.mutate({ installmentId: i.id, paid: false })}>Zurücknehmen</button>}
                  </td>
                </tr>
              ))}
              {f.installments.length === 0 && <tr><td colSpan={9} className="empty">Kein Zahlungsplan.</td></tr>}
            </tbody>
          </table>
        </div>
      </CollapsibleCard>

      <RecordDocuments collapsible referenceKind="financing" referenceId={id} hint="Vertrag, Tilgungsplan und Schriftverkehr mit dem Geldgeber." defaultCategory="other" />
      <EntityHistory entity="financing" id={id} />
    </>
  );
}
